import {
    ChangeDetectionStrategy,
    Component,
    OnChanges,
    OnInit,
    SimpleChanges,
    input,
    output,
} from '@angular/core';

import { ProductSizeSelectorTransformer } from '../../../application/transformers/product-size-selector.transformer';
import { shouldSkipOnSelectUseCase, shouldCheckAvailabilityOnSelectUseCase, mapSizesOnSelectUseCase } from '../../../application/use-cases/product-size-selector.use-cases';
import { ProductSizeSelectorModeEnum } from '../../../domain/constants/product-size-selector.constants';
import { ProductSizeSelectorMode, SelectorProductSize } from '../../../domain/models/product-size-selector.model';
import { ProductSizeSelectorHorizontalComponent } from '../../components/product-size-selector-horizontal/product-size-selector-horizontal.component';
import { ProductSizeSelectorSelectComponent } from '../../components/product-size-selector-select/product-size-selector-select.component';

import { ProductPublicConstants, ProductPublicModels } from '@features/products/product';

@Component({
    selector: 'product-size-selector-layout',
    templateUrl: './product-size-selector-layout.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ProductSizeSelectorHorizontalComponent, ProductSizeSelectorSelectComponent,
    ],
    standalone: true,
})
export class ProductSizeSelectorLayoutComponent implements OnInit, OnChanges {
    public _sizes: SelectorProductSize[] = [];

    public addQuickly = input<boolean>(false);

    public sizes = input<ProductPublicModels.PublicProductSize[]>();

    public isQuickPurchase = input<boolean>(false);

    public mode = input<ProductSizeSelectorMode>('STANDARD');

    public product = input<ProductPublicModels.PublicProduct>();

    public readonly sizeSelectedEvent = output<ProductPublicModels.PublicProductSize>();

    public readonly sizesSelectedEvent = output<ProductPublicModels.PublicProductSize[]>();

    public readonly checkStockAvailability = output<ProductPublicModels.PublicProductSize>();

    public uniqueSize: boolean;

    public selectedSizes: SelectorProductSize[] = [];

    public productAvailability = ProductPublicConstants.PublicProductAvailabilityEnum;

    public productSizeMode = ProductSizeSelectorModeEnum;

    public ngOnInit(): void {
        this.uniqueSize = this.checkIfUniqueSize();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.sizes
            || changes.mode
            || changes.selectedSize
            || changes?.product?.currentValue?.status?.selectedSize === null
        ) {
            this.transformToSelectorProductSize(changes);
        }

        if (changes.sizes) {
            this.uniqueSize = this.checkIfUniqueSize();
        }
    }

    private transformToSelectorProductSize(changes: SimpleChanges) {
        const sizes = changes.sizes && Array.isArray(changes.sizes.currentValue)
            ? changes.sizes.currentValue
            : this._sizes || [];

        const mode = changes.mode ? changes.mode.currentValue : this.mode();
        const productSize = changes?.product?.currentValue?.status?.selectedSize;

        this._sizes = sizes
            .map((size) => ProductSizeSelectorTransformer.toSelectorProductSize(size, productSize, mode));
    }

    public selectSize(selectedSize: SelectorProductSize): void {
        if (shouldSkipOnSelectUseCase(selectedSize, this.mode())) {
            return;
        }

        if (shouldCheckAvailabilityOnSelectUseCase(selectedSize, this.mode())) {
            this.selectedSizes = [];
            this.checkStockAvailability.emit(selectedSize);
            return;
        }

        const index = this.selectedSizes.findIndex(item => item.sku === selectedSize.sku);
        if (index !== -1) {
            this.selectedSizes.splice(index, 1);
        } else {
            this.selectedSizes.push(selectedSize);
        }

        this._sizes = mapSizesOnSelectUseCase(this._sizes, selectedSize, this.mode());

        if (this.mode() === this.productSizeMode.ESSENTIAL_MULTI) {
            this.sizesSelectedEvent.emit(this.selectedSizes);
        } else {
            this.sizeSelectedEvent.emit(selectedSize);
        }
    }

    private checkIfUniqueSize(): boolean {
        return !!this.product()?.status?.selectedColor?.uniqueSize;
    }
}
